import { skipToken } from '@apollo/client'
import { CircleX, ExternalLink } from 'lucide-react'
import { Suspense, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { BranchSelector } from '@/components/form/branch-selector'
import { PrivateGitSelectorFallback } from '@/components/form/private-git-selector/fallback'
import { RepositorySelector } from '@/components/form/repository-selector'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { gitAuthUrls } from '@/config/git-auth-urls'
import { TOKEN_KEY } from '@/config/local-storage'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery } from '@/lib/hooks'
import { BitBucketLogo, GitHubLogo, GitLabLogo } from '@/logos'
import type {
  AuthedServicesQuery,
  AuthedServicesQueryVariables,
  GitBlockQuery,
  GitBlockQueryVariables,
} from '@/components/form/private-git-selector/index.types.gen'
import type { GitType } from '@/graphql.types'

const AUTHED_SERVICES = gql`
  query AuthedServices @live {
    githubInstallAppUrl
    user {
      id
      authedGitServices @live {
        authedServices {
          github
          bitbucket
          gitlab
        }
      }
    }
  }
`

const GIT_BLOCK_QUERY = gql`
  query GitBlock($gitType: GitType!) {
    user {
      id
      isGitAccountBlocked(gitType: $gitType)
    }
  }
`

type FormValues = {
  gitType?: string
  idRemoteRepository: string | null
  displayName: string | null
  defaultBranch: string | null
}

type Props = {
  showBranchSelector?: boolean
}

const PrivateGitSelectorContent = ({ showBranchSelector = true }: Props) => {
  const form = useFormContext<FormValues>()
  const { resetField, watch } = form
  const gitType = watch('gitType')

  const state = encodeURIComponent(
    JSON.stringify({
      action: 'authGit',
      api: import.meta.env.VITE_GRAPHQL_SERVER_URI,
      redirectUri: window.location.origin + window.location.pathname,
      token: localStorage.getItem(TOKEN_KEY),
    })
  )

  const { data: gitBlockData } = useSuspenseQuery<
    GitBlockQuery,
    GitBlockQueryVariables
  >(
    GIT_BLOCK_QUERY,
    gitType
      ? {
          variables: { gitType: gitType as GitType },
        }
      : skipToken
  )

  const { data: authedServicesData } = useSuspenseQuery<
    AuthedServicesQuery,
    AuthedServicesQueryVariables
  >(AUTHED_SERVICES)

  const githubInstallAppUrl = authedServicesData?.githubInstallAppUrl ?? ''
  const authedServices = useMemo(
    () => authedServicesData?.user?.authedGitServices?.authedServices,
    [authedServicesData]
  )

  const isGitAccountBlocked = useMemo(
    () => gitBlockData?.user?.isGitAccountBlocked ?? false,
    [gitBlockData]
  )

  const isGitConnected =
    authedServices?.[gitType as keyof typeof authedServices] ?? false
  const gitAuthUrl = gitAuthUrls[gitType as keyof typeof gitAuthUrls] ?? ''

  useEffect(() => {
    return () => {
      resetField('gitType')
    }
  }, [resetField])

  const renderConnectButton = () => {
    const gitMap = {
      github: { title: 'GitHub', logo: <GitHubLogo className="size-4" /> },
      gitlab: { title: 'GitLab', logo: <GitLabLogo className="size-4" /> },
      bitbucket: {
        title: 'Bitbucket',
        logo: <BitBucketLogo className="size-4" />,
      },
    }
    const { title, logo } = gitMap[gitType as keyof typeof gitMap] || {
      title: 'Git Service',
      logo: null,
    }
    return (
      <FormField
        control={form.control}
        name="idRemoteRepository"
        render={() => (
          <FormItem className="w-fit">
            <FormLabel>Repository</FormLabel>
            <FormControl>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  window.open(`${gitAuthUrl}&state=${state}`, '_blank')
                }
              >
                {logo} Connect to {title}
              </Button>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="gitType"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel>Git provider</FormLabel>
            <FormControl>
              <Tabs value={field.value} onValueChange={field.onChange}>
                <TabsList>
                  <TabsTrigger className="px-4" value="github">
                    <GitHubLogo className="size-4" /> GitHub
                  </TabsTrigger>
                  <TabsTrigger className="px-4" value="gitlab">
                    <GitLabLogo className="size-4" /> GitLab
                  </TabsTrigger>
                  <TabsTrigger className="px-4" value="bitbucket">
                    <BitBucketLogo className="size-4" /> Bitbucket
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isGitAccountBlocked ? (
        <Item variant="muted">
          <ItemMedia>
            <CircleX className="fill-destructive stroke-muted" />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>Git account blocked</ItemTitle>
            <ItemDescription>
              This account is temporarily blocked by fraud prevention.
              <br />
              Add a payment method to verify, then contact support with your
              handle to lift the block.
            </ItemDescription>
          </ItemContent>
        </Item>
      ) : isGitConnected ? (
        <div>
          {showBranchSelector ? (
            <div className="grid gap-4 grid-cols-3 items-start">
              <div className="col-span-2">
                <RepositorySelector />
              </div>
              <div className="col-span-1">
                <BranchSelector />
              </div>
            </div>
          ) : (
            <RepositorySelector />
          )}
          {gitType === 'github' && githubInstallAppUrl ? (
            <a href={githubInstallAppUrl} target="_blank">
              <Muted className="mt-1 underline flex items-center gap-1">
                Adjust GitHub repository access
                <ExternalLink className="size-3.5" />
              </Muted>
            </a>
          ) : null}
        </div>
      ) : (
        renderConnectButton()
      )}
    </div>
  )
}

export const PrivateGitSelector = ({ showBranchSelector }: Props) => {
  return (
    <Suspense fallback={<PrivateGitSelectorFallback />}>
      <PrivateGitSelectorContent showBranchSelector={showBranchSelector} />
    </Suspense>
  )
}
